import { useMemo, useState } from "react";
import { SCI_PALETTE } from "@/lib/science/sci-chart";
import { SciTextArea } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { parseVennSets, vennRegions } from "../lib/venn";

const EXAMPLE = "Set A,Set B,Set C\n1,3,4\n2,4,6\n3,5,7\n4,6,8";

const countStyle = {
  fill: "var(--text-base)",
  fontSize: 16,
  fontWeight: 700,
  textAnchor: "middle",
} as const;
const nameStyle = {
  fill: "var(--text-secondary)",
  fontSize: 14,
  fontWeight: 600,
  textAnchor: "middle",
} as const;

export function VennPlot() {
  const [text, setText] = useState(EXAMPLE);
  const parsed = useMemo(() => parseVennSets(text), [text]);
  const regions = useMemo(() => vennRegions(parsed), [parsed]);

  return (
    <div className="sci-card rounded-2xl p-6 sm:p-8">
      <SciTextArea
        id="venn-input"
        label={m.venn_input_label()}
        value={text}
        placeholder={m.venn_placeholder()}
        onChange={setText}
        rows={6}
      />
      <div className="mt-2">
        <button
          type="button"
          onClick={() => setText(EXAMPLE)}
          className="sci-chip rounded-md px-2 py-1 text-xs transition-opacity hover:opacity-80"
        >
          {m.plot_load_example()}
        </button>
      </div>

      {regions == null ? (
        <p className="mt-5 text-sm text-muted-foreground">{m.venn_hint()}</p>
      ) : regions.count === 2 ? (
        <svg
          data-testid="venn-chart"
          viewBox="0 0 460 300"
          className="mt-5 w-full"
          role="img"
          aria-label="Venn diagram"
        >
          <title>Venn diagram</title>
          <circle
            cx="180"
            cy="150"
            r="110"
            fill={SCI_PALETTE[0]}
            fillOpacity="0.4"
            stroke={SCI_PALETTE[0]}
          />
          <circle
            cx="280"
            cy="150"
            r="110"
            fill={SCI_PALETTE[1]}
            fillOpacity="0.4"
            stroke={SCI_PALETTE[1]}
          />
          <text x="110" y="45" style={nameStyle}>
            {parsed.names[0]}
          </text>
          <text x="350" y="45" style={nameStyle}>
            {parsed.names[1]}
          </text>
          <text x="120" y="156" style={countStyle}>
            {regions.regions.A}
          </text>
          <text x="230" y="156" style={countStyle}>
            {regions.regions.AB}
          </text>
          <text x="340" y="156" style={countStyle}>
            {regions.regions.B}
          </text>
        </svg>
      ) : (
        <svg
          data-testid="venn-chart"
          viewBox="0 0 460 380"
          className="mt-5 w-full"
          role="img"
          aria-label="Venn diagram"
        >
          <title>Venn diagram</title>
          <circle
            cx="230"
            cy="150"
            r="115"
            fill={SCI_PALETTE[0]}
            fillOpacity="0.38"
            stroke={SCI_PALETTE[0]}
          />
          <circle
            cx="170"
            cy="250"
            r="115"
            fill={SCI_PALETTE[1]}
            fillOpacity="0.38"
            stroke={SCI_PALETTE[1]}
          />
          <circle
            cx="290"
            cy="250"
            r="115"
            fill={SCI_PALETTE[2]}
            fillOpacity="0.38"
            stroke={SCI_PALETTE[2]}
          />
          <text x="230" y="28" style={nameStyle}>
            {parsed.names[0]}
          </text>
          <text x="70" y="345" style={nameStyle}>
            {parsed.names[1]}
          </text>
          <text x="390" y="345" style={nameStyle}>
            {parsed.names[2]}
          </text>
          <text x="230" y="110" style={countStyle}>
            {regions.regions.A}
          </text>
          <text x="140" y="270" style={countStyle}>
            {regions.regions.B}
          </text>
          <text x="320" y="270" style={countStyle}>
            {regions.regions.C}
          </text>
          <text x="175" y="205" style={countStyle}>
            {regions.regions.AB}
          </text>
          <text x="285" y="205" style={countStyle}>
            {regions.regions.AC}
          </text>
          <text x="230" y="300" style={countStyle}>
            {regions.regions.BC}
          </text>
          <text x="230" y="235" style={countStyle}>
            {regions.regions.ABC}
          </text>
        </svg>
      )}
    </div>
  );
}
